import { useState, useRef, useMemo, useCallback, useEffect, useLayoutEffect } from 'react';
import { Table } from 'src/components/ui';
import styled from '@emotion/styled';
import { isRequired } from '../ArrayFieldTemplate/util'; // NOTE: Move this later
import classnames from 'src/util/classnames';
import { useTheme } from '@emotion/react';
import { set } from 'lodash';
import { BG, SURFACE, RAISED, useDarkMode } from 'src/context/DarkModeContext';
import { useJnx } from 'src/util/jnx';
import { useSideChannelSubscription } from 'src/util/useSideChannel';
import getPathFromId from 'src/util/getPathFromId';
import ObjectTableContext from '../ObjectTableContext';
import { lighten } from 'polished';

const LIGHT_BG_ALIASES = new Set(['white', '#ffffff', '#fff', '#f9f9f9']);
const LIGHT_HOVER = '#eef1f4';

const MIN_COLUMN_WIDTH = 80;
const MAX_COLUMN_WIDTH = 600;
const DRAG_THRESHOLD_PX = 4;

const CELL_BG = lighten(0.05, BG);

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// How far (0–100) the scroll container is scrolled from its left edge, and how much
// is left to scroll on its right.
const computeScrollPercentages = (horizontalScroll, { scrollLeft, scrollWidth, clientWidth }) => {
  if (!horizontalScroll) return { left: 0, right: 1 };
  if (scrollWidth <= clientWidth) return { left: 0, right: 100 };
  const scrolledLeft = (scrollLeft / (scrollWidth - clientWidth)) * 100;
  return { left: clamp(scrolledLeft, 0, 100), right: clamp(100 - scrolledLeft, 0, 100) };
};

const resolveCellBg = (darkMode, indent, asHeader) => {
  if (+indent > 0) {
    return darkMode ? CELL_BG : 'white';
  }

  if (asHeader) {
    return darkMode ? RAISED : '#f9f9f9';
  }

  return darkMode ? SURFACE : '#f9f9f9';
};

const isCellInStickyRow = (stickyHeaders, asHeader, index) => {
  if (!stickyHeaders) return false;
  return asHeader || index === 0;
};

const isCellInStickyColumn = (stickyColumns, index) => {
  if (!stickyColumns || stickyColumns <= 0) return false;
  let position = (index += 1);
  return position <= stickyColumns;
};

const StyledTd = styled.td`
  ${props =>
    props.indent
      ? `
    padding-left: ${props.indent * 20}px !important;
  `
      : ''}
  border-color: ${props => (props.darkMode ? 'rgba(255, 255, 255, 0.07) ' : '#e0e0e0')};
  line-height: 1;
  text-align: ${props => props.header?.textAlign ?? 'left'};
  vertical-align: ${props => props.header?.verticalAlign ?? 'middle'};
  background: ${props => props.rowBg};

  tr:hover > & {
    background: ${props => props.hoverBg} !important;
  }

  ${props =>
    props.stickyColumnCell
      ? `&::after {
  content: '';
  position: absolute;
  right: -40px;
  height: 100%;
  width: 40px;
  top: 0;
  border: 1px solid #a3a3a3;
  background: linear-gradient(to right, #87848440 0%, transparent calc(var(--ot-scroll-left, 0) * 1%));
  }`
      : ''}
`;

const parseCellProps = (
  header,
  uiSchema,
  {
    stickyInfo = {},
    stickyColumns,
    columnWidth,
    isSingleCellFullWidth = false,
    scrollWidth,
    cellIdx,
    asHeader,
    isLastCellInRow,
    isDarkMode = false,
    rowStyles = {},
  }
) => {
  const {
    'ui:cellClassName': cellClassName,
    'ui:colSpan': colSpan,
    'ui:rowSpan': rowSpan,
    'ui:hideLabel': hideLabel,
    'ui:cellColor': cellColor,
    'ui:indent': indent,
  } = uiSchema;

  const { colSpan: headerColSpan, verticalAlign: headerVerticalAlign, textAlign: headerTextAlign, indent: headerIndent } = header;

  // Sticky-positioning only — whether/how this cell pins itself while scrolling.
  // Sizing (below) is a separate concern: any column can carry an explicit width
  // once the user has resized it, whether or not it's also sticky.
  const stickyStyles = () => {
    if ((stickyInfo == null || (!stickyInfo.row && !stickyInfo.col)) && !isSingleCellFullWidth) return {};

    let styles = {};
    if (stickyInfo.col || isSingleCellFullWidth) {
      styles.left = 0;
      styles.zIndex = 10;
      styles.position = 'sticky';
    }

    if (stickyInfo.col && stickyInfo.row) {
      styles.zIndex = 30;
    }

    if (isSingleCellFullWidth) {
      styles.position = 'sticky';
      styles.zIndex = 15;
      styles.height = rowStyles.height ?? 'auto';
    }

    return styles;
  };

  // Explicit box sizing — full-width rows stretch to the measured scroll container;
  // any other column applies its resized/default width, if it has one, else stays
  // auto/content-sized exactly as before.
  const sizeStyles = () => {
    if (isSingleCellFullWidth) {
      // scrollWidth starts as 'auto' (string) until the scroll container is measured;
      // subtracting from a string produces NaN, which React rejects as a CSS value.
      if (typeof scrollWidth !== 'number' || scrollWidth <= 0) return {};
      return {
        width: scrollWidth - 3,
        minWidth: scrollWidth - 3,
        maxWidth: scrollWidth - 3,
        overflow: 'hidden',
        // 'normal' (not 'pre-wrap') — still wraps at the fixed width, but doesn't
        // preserve/render incidental whitespace between block elements (e.g. the
        // markdown blurb's separate <p> tags), which otherwise inflates the gap
        // between paragraphs beyond their actual margin.
        whiteSpace: 'unset',
      };
    }
    if (columnWidth) {
      return {
        width: columnWidth,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        overflow: 'hidden',
        // 'normal' (not 'pre-wrap') — still wraps at the fixed width, but doesn't
        // preserve/render incidental whitespace between block elements (e.g. the
        // markdown blurb's separate <p> tags), which otherwise inflates the gap
        // between paragraphs beyond their actual margin.
        whiteSpace: 'unset',
      };
    }
    return {};
  };

  const cellStyle = {
    padding: '10px 7px',
    lineHeight: 1,
    textAlign: headerTextAlign || 'left',
    verticalAlign: headerVerticalAlign || 'middle',
    background: resolveCellBg(isDarkMode, indent, asHeader),
    // Ensures the resize-handle overlay (position: absolute) anchors to this cell,
    // not some distant positioned ancestor. stickyStyles() overrides to 'sticky' when relevant.
    position: 'relative',
    ...stickyStyles(),
    ...sizeStyles(),
  };

  const _colSpan = colSpan === 'max' ? 1000 : colSpan;
  const isLastStickyCellInRow = !isLastCellInRow && cellIdx + 1 === stickyColumns && !isSingleCellFullWidth && stickyInfo.col;

  return {
    className: classnames(cellClassName, `animated-show`),
    colSpan: _colSpan || headerColSpan,
    rowSpan: rowSpan,
    style: cellStyle,
    stickyColumnCell: isLastStickyCellInRow,
    indent: indent ?? headerIndent ?? 0,
  };
};

/**
 * Column and header styling options
 * @typedef {Object} TableHeader
 * @property {string} text - Header label text.
 * @property {number} [colSpan]
 * @property {string} [className]
 * @property {string} [verticalAlign]
 * @property {string} [textAlign]
 * @property {string} [indent]
 */

/**
 * Options that control the rendering of the cells in the table
 *
 * @typedef {Object} ObjectTableCellUIOptions
 * @property {string | undefined} [ui_cellClassName] - (actual key: `ui:cellClassName`)
 * @property {number | undefined} [ui_colSpan] - (actual key: `ui:colSpan`)
 * @property {boolean | undefined} [ui_hideLabel] - (actual key: `ui:hideLabel`)
 * @property {string | undefined} [ui_cellColor] - (actual key: `ui:cellColor`)
 * @property {number | undefined} [ui_rowSpan] - (actual key: `ui:rowSpan`)
 * @property {number | undefined} [ui_indent] - (actual key: `ui:indent`)
 */

/**
 * Options that control the rendering of the rows in the table
 *
 * @typedef {(Object & {[name: string]: ObjectTableCellUIOptions})} ObjectTableRowUIOptions
 *

/**
 * Options that control the rendering of an ObjectTable component.
 * These keys correspond to `ui:*` fields in a JSON Schema `uiSchema`.
 *
 * @typedef {Object} ObjectTableUIOptions
 * @property {TableHeader[]} [ui_headers] - (actual key: `ui:headers`) Optional table header definitions.
 * @property {boolean} [ui_bordered] - (actual key: `ui:bordered`) Whether the table should have borders.
 * @property {boolean} [ui_hasButtons] - (actual key: `ui:hasButtons`) Whether to render an extra column for action buttons.
 */

/**
 * @typedef {Object} IdSchema
 * @property {string} $id - The unique identifier for this schema node.
 * @property {Record<string, (IdSchema|string)>} [key] - Nested field schemas or string IDs.
 */

/**
 * Renders a table representation of an object field based on the given schema and UI schema.
 *
 * @param {Object} props
 * @param {Object} props.schema - The JSON schema defining the structure of the object.
 * @param {(Object & ObjectTableUIOptions & {[name: string]: ObjectTableRowUIOptions})} props.uiSchema - The UI schema defining layout and presentation options.
 * @param {string} [props.className] - Optional CSS class name for custom styling.
 * @param {IdSchema} props.idSchema - Schema providing unique IDs for each field.
 * @param {Object} props.registry - The registry containing form fields, templates, and widgets.
 * @param {Record<string, { content: { props: import('@rjsf/core').FieldProps } }>} props.properties - Map of property names to row field definitions.
 *
 * @returns {JSX.Element} A table element displaying the object’s properties as rows.
 */
function ObjectTable({ schema, formData, uiSchema, className, fieldPathId, registry, properties: rowFields, ...rest }) {
  const { $id } = fieldPathId;
  const {
    'ui:headers': headers,
    'ui:borderless': borderless,
    'ui:hasButtons': uiHasButtons,
    'ui:horizontalScroll': horizontalScrollFromSchema = false,
    'ui:stickyColumns': stickyColumnsFromSchema = 0,
    'ui:stickyColumnWidth': stickyColumnWidthFromSchema = 'auto',
    'ui:stickyHeaders': stickyHeaders = false,
    'ui:disableIf': uiDisableIf,
  } = uiSchema;

  const theme = useTheme();
  const { isDarkMode } = useDarkMode();

  const formContext = registry?.formContext;
  const rootFormData = useSideChannelSubscription(formContext?.sideChannel, 0);
  const dataPath = useMemo(() => getPathFromId(fieldPathId.$id), [fieldPathId.$id]);
  const disableIfJnx = useJnx(uiDisableIf);
  const disabledByTable = useMemo(
    () =>
      disableIfJnx &&
      disableIfJnx.eval(rootFormData || {}, dataPath, {
        rootData: rootFormData,
        formContext,
      }),
    [disableIfJnx, rootFormData, dataPath, formContext]
  );

  const stickyHeadersRef = useRef({});
  const scrollRef = useRef();
  const scrollContainerRef = useRef();
  const [scrollRefData, setScrollRefData] = useState({ init: false, width: 'auto', scrollWidth: window.screen.width });

  const formDataRef = useRef(formData ?? {});
  formDataRef.current = formData ?? {};

  const { rows, stickyCells, cellColumnIndex, columnCount, stickyColumns } = useObjectTable(
    rowFields,
    stickyColumnsFromSchema,
    stickyColumnWidthFromSchema,
    stickyHeaders
  );
  const defaultStickyColumnWidth = stickyColumnWidthFromSchema !== 'auto' ? stickyColumnWidthFromSchema : columnCount > 2 ? '250px' : 'auto';

  // A header entry's own colSpan can also span multiple grid columns, so its array
  // position doesn't always match its true column index either — accumulate it the
  // same way cellColumnIndex does for body cells.
  const headerColumnIndexes = useMemo(() => {
    let col = 0;
    return (headers || []).map(h => {
      const trueColIdx = col;
      col += h.colSpan || 1;
      return trueColIdx;
    });
  }, [headers]);

  // Every non-full-width column can be drag-resized, sticky or not — widths aren't
  // persisted, they just reset on reload. Keyed by the cell's TRUE occupied grid
  // column (see cellColumnIndex in useObjectTable below), not its raw position within
  // a row's own property list — those differ once colSpan/rowSpan are in play.
  const [columnWidthOverrides, setColumnWidthOverrides] = useState({});

  const getColumnWidth = colIdx => {
    const overridePx = columnWidthOverrides[colIdx];
    if (overridePx != null) return `${overridePx}px`;
    // Sticky columns fall back to the schema/computed default; other columns stay
    // auto/content-sized via table-layout:fixed exactly as before, until resized.
    return colIdx < stickyColumns ? defaultStickyColumnWidth : undefined;
  };

  // Once any column has been resized, the table needs real horizontal scroll (rather
  // than shrinking to min-content) so growing a column doesn't just steal space from
  // its siblings — this only kicks in reactively, so untouched tables render exactly
  // as before.
  const hasResizedColumns = Object.keys(columnWidthOverrides).length > 0;
  const horizontalScroll = horizontalScrollFromSchema || columnCount > 2 || hasResizedColumns;

  const resetColumnWidth = useCallback(colIdx => {
    setColumnWidthOverrides(prev => {
      if (!(colIdx in prev)) return prev;
      const next = { ...prev };
      delete next[colIdx];
      return next;
    });
  }, []);

  // Drag-to-resize for a column's right edge. Uses Pointer Events with capture so the
  // drag keeps tracking even if the pointer leaves the thin handle — no document-level
  // listeners needed. Width isn't persisted; it resets on reload.
  const handleColumnResizeStart = useCallback((event, colIdx) => {
    event.preventDefault();
    event.stopPropagation();

    const handleEl = event.currentTarget;
    const cellEl = handleEl.closest('td, th');
    const rowEl = cellEl?.closest('tr');
    const startX = event.clientX;
    const startWidth = cellEl ? cellEl.getBoundingClientRect().width : MIN_COLUMN_WIDTH;

    handleEl.setPointerCapture(event.pointerId);

    // Nothing below runs until the pointer has actually moved past this threshold —
    // a bare click (press + release with no real movement, or an incidental brush
    // against the handle) must have zero effect: no frozen widths, no cursor change,
    // no scroll-mode switch. Only a deliberate drag counts as a resize.
    let dragStarted = false;
    const latestWidthRef = { current: startWidth };
    let rafId = null;

    // table-layout:fixed divides any column WITHOUT an explicit width from whatever
    // space is left over, shared equally among all such columns — so leaving siblings
    // at 'auto' means growing this one reflows every other unresized column too.
    // Freeze every column's current rendered width the first time a real drag starts,
    // so each column gets its own explicit width and none are subject to that
    // redistribution. Already-resized columns are left alone.
    //
    // Read each cell's true column off its data-col-index attribute rather than its
    // DOM position — a rowSpan from an earlier row can cover a column entirely, so
    // this row simply won't render a <td> for it, and position no longer lines up
    // with column index once that happens.
    const freezeSiblingWidths = () => {
      if (!rowEl) return;
      setColumnWidthOverrides(prev => {
        let changed = false;
        const next = { ...prev };
        Array.from(rowEl.children).forEach(cell => {
          const cellColIdxAttr = cell.dataset?.colIndex;
          if (cellColIdxAttr == null) return;
          const cellColIdx = Number(cellColIdxAttr);
          if (cellColIdx in next) return;
          next[cellColIdx] = cell.getBoundingClientRect().width;
          changed = true;
        });
        return changed ? next : prev;
      });
    };

    const flush = () => {
      rafId = null;
      setColumnWidthOverrides(prev => ({ ...prev, [colIdx]: latestWidthRef.current }));
    };

    const handlePointerMove = moveEvent => {
      const delta = moveEvent.clientX - startX;
      if (!dragStarted) {
        if (Math.abs(delta) < DRAG_THRESHOLD_PX) return;
        dragStarted = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        freezeSiblingWidths();
      }
      latestWidthRef.current = clamp(startWidth + delta, MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH);
      if (rafId == null) rafId = requestAnimationFrame(flush);
    };

    const stopResize = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      handleEl.releasePointerCapture(event.pointerId);
      handleEl.removeEventListener('pointermove', handlePointerMove);
      handleEl.removeEventListener('pointerup', stopResize);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    handleEl.addEventListener('pointermove', handlePointerMove);
    handleEl.addEventListener('pointerup', stopResize);
  }, []);

  const hasButtons = uiHasButtons || false;

  // Shared prefix/suffix addon widths for every field in this table (see
  // ObjectTableContext) — each field reports its own measured width, and the max only
  // ever grows within a session. Prefix/suffix values don't change once a form has
  // rendered, so there's no need to track per-field widths just to shrink the max back
  // down later. Prefixes and suffixes are tracked independently since they aren't
  // visually related.
  const [maxPrefixWidth, setMaxPrefixWidth] = useState(undefined);
  const [maxSuffixWidth, setMaxSuffixWidth] = useState(undefined);

  const reportPrefixWidth = useCallback(width => {
    setMaxPrefixWidth(prev => (prev == null || width > prev ? width : prev));
  }, []);

  const reportSuffixWidth = useCallback(width => {
    setMaxSuffixWidth(prev => (prev == null || width > prev ? width : prev));
  }, []);

  const objectTableContextValue = useMemo(() => ({ reportPrefixWidth, reportSuffixWidth, maxPrefixWidth, maxSuffixWidth }), [
    reportPrefixWidth,
    reportSuffixWidth,
    maxPrefixWidth,
    maxSuffixWidth,
  ]);

  // The scroll position never goes through React state — that re-rendered the whole
  // table (and every rjsf field in it) on each scroll frame. Only the container's
  // dimensions live in state (they change on resize, not on scroll); the fade
  // gradients read the position from CSS variables written straight onto the
  // container element.
  const horizontalScrollRef = useRef(horizontalScroll);
  horizontalScrollRef.current = horizontalScroll;

  const lastScrollVarsRef = useRef({});

  // Reads every scroll metric once, then writes — a read after a write would force a
  // synchronous style + layout pass over the whole table on every scroll frame. The
  // CSS variables are inherited by every cell, so they're rounded to whole percents
  // and only rewritten when they actually change (never, during a vertical scroll).
  // Scroll events already fire at most once per frame, so no throttling is needed —
  // and with nothing deferred, there's nothing left to fire after unmount.
  const handleTableScroll = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollEl;

    setScrollRefData(prev =>
      prev.init && prev.width === clientWidth && prev.scrollWidth === scrollWidth ? prev : { init: true, width: clientWidth, scrollWidth }
    );

    const containerEl = scrollContainerRef.current;
    if (!containerEl) return;
    const percentages = computeScrollPercentages(horizontalScrollRef.current, { scrollLeft, scrollWidth, clientWidth });
    const left = Math.round(percentages.left);
    const right = Math.round(percentages.right);
    const last = lastScrollVarsRef.current;
    if (last.left !== left) containerEl.style.setProperty('--ot-scroll-left', String(left));
    if (last.right !== right) containerEl.style.setProperty('--ot-scroll-right', String(right));
    lastScrollVarsRef.current = { left, right };
  }, []);

  // Measures on mount, and re-derives the gradients whenever what they depend on
  // (other than the scroll position itself) changes — e.g. a column resize switching
  // horizontal scroll on.
  useLayoutEffect(() => {
    handleTableScroll();
  }, [handleTableScroll, horizontalScroll, scrollRefData]);

  useEffect(() => {
    const currentElement = scrollRef.current;

    if (currentElement) {
      currentElement.addEventListener('scroll', handleTableScroll);
      window.addEventListener('resize', handleTableScroll);
    }

    return () => {
      if (currentElement) {
        currentElement.removeEventListener('scroll', handleTableScroll);
        window.removeEventListener('resize', handleTableScroll);
      }
    };
  }, [handleTableScroll]);

  useLayoutEffect(() => {
    if (stickyHeaders) {
      const headerKeys = Object.keys(stickyHeadersRef.current);
      if (headerKeys.length > 0) {
        let heightFromTop = 0;
        headerKeys.forEach(key => {
          let rowRef = stickyHeadersRef.current[key];
          rowRef.style.top = `${heightFromTop}px`;
          rowRef.style.position = 'sticky';
          rowRef.style.zIndex = key === `0` ? 29 - Number(key) : 20 - Number(key);
          heightFromTop += rowRef.offsetHeight;
        });
      }
    }
  }, [stickyHeaders]);

  const renderTable = () => (
    <Table
      bordered={!borderless}
      hover={true}
      className="animated-show rjsf-object-table"
      style={{
        overflow: 'visible',
        background: 'transparent',
        tableLayout: 'fixed',
        borderCollapse: 'separate',
        width: columnCount > 2 || hasResizedColumns ? 'max-content' : 'min-content',
        minWidth: '100%',
        margin: '0 auto',

        // DO NOT set overflow or maxHeight here — <table> is not a valid scroll
        // container. The wrapper div in renderWithScroll() owns that responsibility.
      }}
    >
      {headers && headers.length > 0 && (
        <thead>
          <tr
            className="animated-show for-object-row"
            ref={element => {
              if (element && stickyHeaders) {
                stickyHeadersRef.current = { 0: element };
              } else {
                stickyHeadersRef.current = {};
              }
            }}
          >
            {headers?.map((h, idx) => {
              const { text: headerText, colSpan: headerColSpan, className: headerClassName, indent: headerIndent } = h;
              const trueColIdx = headerColumnIndexes[idx];
              const isSticky = isCellInStickyColumn(stickyColumns, trueColIdx);
              const columnWidth = getColumnWidth(trueColIdx);
              return (
                <th
                  key={`table-header-${idx}`}
                  className={classnames(headerClassName)}
                  colSpan={headerColSpan}
                  data-col-index={trueColIdx}
                  style={{
                    position: 'relative',
                    verticalAlign: 'middle',
                    paddingLeft: headerIndent ? `${headerIndent}em` : undefined,
                    ...(isSticky
                      ? {
                          position: 'sticky',
                          left: 0,
                          backgroundColor: isDarkMode ? RAISED : 'white',
                        }
                      : {}),
                    ...(columnWidth ? { width: columnWidth, minWidth: columnWidth, maxWidth: columnWidth } : {}),
                  }}
                >
                  {headerText}
                  <div
                    className="object-table-col-resize-handle"
                    onPointerDown={event => handleColumnResizeStart(event, trueColIdx)}
                    onDoubleClick={() => resetColumnWidth(trueColIdx)}
                  />
                </th>
              );
            })}
            {hasButtons ? <th /> : null}
          </tr>
        </thead>
      )}

      <tbody key={`table-${$id}`}>
        {rows.map(({ content: { props }, name }, idx) => (
          // How to pass rest of props?
          <ObjectTableRow
            key={idx}
            headers={headers}
            hasButtons={hasButtons}
            rowIdx={idx}
            isDarkMode={isDarkMode}
            fieldPathId={fieldPathId}
            horizontalScroll={horizontalScroll}
            refFn={element => {
              if (stickyHeaders) {
                if (element) {
                  stickyHeadersRef.current[idx] = element;
                } else {
                  delete stickyHeadersRef.current[idx];
                }
              }
            }}
            stickyHeaders={stickyHeaders}
            stickyCells={stickyCells}
            cellColumnIndex={cellColumnIndex}
            stickyColumns={stickyColumns}
            getColumnWidth={getColumnWidth}
            onColumnResizeStart={handleColumnResizeStart}
            onResetColumnWidth={resetColumnWidth}
            scrollWidth={scrollRefData.width}
            columnCount={columnCount}
            formDataRef={formDataRef}
            {...props}
            disabled={props.disabled || disabledByTable || false}
          />
        ))}
      </tbody>
    </Table>
  );

  // Representative width of the sticky region, for the scroll-indicator gradient's
  // offset — mirrors the pre-existing assumption that all sticky columns share one
  // width (real for the common stickyColumns <= 1 case; sticky columns beyond the
  // first already overlap today regardless of this indicator).
  const stickyRegionWidth = stickyColumns > 0 ? getColumnWidth(stickyColumns - 1) : 'auto';

  // Renders the wrapper div that owns `scrollRef`. `enableScroll` toggles between the
  // real horizontal-scroll UX (overflow-x: auto, scroll indicator gradient) and a
  // measurement-only fallback (overflow: visible, no indicator) used when this table
  // doesn't need horizontal scrolling. The wrapper itself is never skipped: full-width
  // cells (colSpan = columnCount) and sticky-column sizing in parseCellProps both read
  // scrollRefData's measured width, which only gets populated once scrollRef is attached
  // to a mounted element — without the wrapper it stays stuck at the initial 'auto'.
  // The fades are 40px wide at most, scaled by the scroll percentages (0–100) that
  // handleTableScroll writes to the container as CSS variables.
  const renderScrollContainer = enableScroll => {
    const leftGradient = stickyColumns > 0 ? 'transparent 0%' : `#87848440 0%, transparent calc(var(--ot-scroll-left, 0) * 0.4px)`;
    const rightGradient = `#87848440 0%, transparent calc(var(--ot-scroll-right, 0) * 0.4px)`;
    return (
      <div ref={scrollContainerRef} className="object-table-scroll-container w-full min-w-0" style={{ position: 'relative' }}>
        <div
          ref={scrollRef}
          className="object-table-scroll-wrapper"
          style={{
            // Explicitly set overflow-x here (not just via CSS class) so it is
            // guaranteed to be the scroll container for position:sticky cells.
            // Any ancestor overflow:hidden would break sticky; this wrapper must
            // be the first overflow:auto ancestor above the <table>.
            overflowX: enableScroll ? 'auto' : 'visible',
            maxWidth: '100%',
            // When sticky headers are active, also scroll vertically.
            ...(enableScroll && stickyHeaders
              ? {
                  maxHeight: `calc(${window.innerHeight}px - calc(${theme.navbarHeight} + var(--form-sticky-header-height, 0px) + 50px))`,
                  overflowY: 'auto',
                }
              : {}),
          }}
        >
          {renderTable()}
        </div>
        {enableScroll && scrollRefData.init && scrollRefData.scrollWidth > scrollRefData.width && (
          <div
            className="object-table-scroll-indicator"
            style={{
              width: `calc(100% - ${stickyRegionWidth === 'auto' ? '0px' : stickyRegionWidth})`,
              left: stickyRegionWidth,
              background: `linear-gradient(to right, ${leftGradient}), linear-gradient(to left, ${rightGradient})`,
            }}
          />
        )}
      </div>
    );
  };

  // Sticky columns ALSO need the scroll wrapper — without it the table would set
  // overflow on the <table> element itself, which doesn't create a valid scroll
  // container for position:sticky to work against.
  return (
    <ObjectTableContext.Provider value={objectTableContextValue}>
      {renderScrollContainer(horizontalScroll || stickyHeaders || stickyColumns > 0)}
    </ObjectTableContext.Provider>
  );
}

/**
 * Renders a single row in an object table.
 *
 * @param {Object} props
 * @param {TableHeader[]} props.headers - Column header definitions.
 * @param {boolean} props.hasButtons - Whether to render a trailing cell for buttons.
 * @param {number} props.rowIdx - Index of the current row.
 * @param {Object} props.schema - JSON schema describing the row’s structure.
 * @param {Object} props.uiSchema - UI schema for customizing row appearance.
 * @param {Object} props.idSchema - Schema for field IDs.
 * @param {Object} props.errorSchema - Validation errors for this row.
 * @param {any} props.formData - Current form data for this row (JSON).
 * @param {Object} props.formContext - Arbitrary form-level context object.
 * @param {Object} props.registry - Registry of fields, widgets, and templates.
 * @param {Function} props.onChange - Callback to propagate form data changes.
 * @param {Function} props.onBlur - Callback when a field loses focus.
 * @param {Function} props.onFocus - Callback when a field gains focus.
 * @param {Function} props.onDropPropertyClick
 * @param {boolean} props.disabled - Whether fields are disabled.
 * @param {boolean} props.readonly - Whether fields are read-only.
 * @param {boolean} props.hideError - Whether to hide validation errors.
 * @param {boolean} [props.isReadOnly] - Alternative flag for read-only state (can be missing).
 * @param {string} [props.idPrefix] - Prefix for field IDs (can be missing).
 * @param {string} [props.idSeparator] - Separator for ID segments (can be missing).
 * @param {Record<string, Jnx>} [props.showColumnsIf] - Optional per-field visibility rules (can be missing).
 *
 * @returns {JSX.Element} The rendered table row.
 */
function ObjectTableRow({ headers, hasButtons, rowIdx, ...props }) {
  const {
    registry,
    disabled,
    hideError,
    formData,
    onBlur,
    onFocus,
    onChange,
    uiSchema,
    errorSchema,
    schema,
    horizontalScroll,
    refFn,
    stickyCells,
    cellColumnIndex,
    stickyColumns,
    getColumnWidth,
    onColumnResizeStart,
    onResetColumnWidth,
    scrollWidth,
    propertyName,
    formDataRef,
    isDarkMode,
    columnCount,
  } = props;
  const formContext = registry?.formContext;

  const { properties: cellFields } = schema;
  const { ':classNames': rowClassNames, 'ui:asHeader': asHeader = false } = uiSchema || {};
  const { SchemaField } = registry.fields;

  const overlayCellRef = useRef();

  const { onDropPropertyClick, onPropertyChange, readonly, fieldPathId } = useObjectTableRowHooks(props);
  const { $id } = fieldPathId;

  const _cellFields = useMemo(() => {
    return Object.entries(cellFields);
  }, [cellFields]);

  const isSingleCellFullWidth = useMemo(() => {
    if (_cellFields.length === 1) {
      let onlyCell = _cellFields[0][1];
      if (onlyCell && onlyCell['ui:colSpan'] === 'max') {
        return true;
      }
      if (onlyCell && onlyCell['ui:colSpan'] >= columnCount) {
        return true;
      }
    }
    return false;
  }, [_cellFields]);

  const rowStyle = useMemo(() => {
    if (!isSingleCellFullWidth || !(scrollWidth > 0)) return {};
    return {
      width: scrollWidth - 2,
      minWidth: scrollWidth - 2,
      maxWidth: scrollWidth - 2,
    };
  }, [isSingleCellFullWidth, scrollWidth]);

  const renderRow = (key, styles = {}, ref) => {
    // The row's background is fixed by its first cell's indent — every other
    // cell in the row shares it, regardless of its own indent.
    let rowBg;

    return (
      <tr
        id={`table-row-${$id}`}
        key={key}
        className={`animated-show for-object-row ${rowClassNames}`}
        style={{ ...rowStyle, ...styles }}
        ref={element => {
          if (asHeader && refFn) {
            refFn(element);
          }
        }}
      >
        {_cellFields.map(([name, fieldSchema], cellIdx) => {
          const fieldUiSchema = uiSchema?.[name] || {};

          // cellIdx is just this cell's position within THIS row's own property list —
          // a colSpan/rowSpan earlier in the table can shift it off the true grid column,
          // so anything keyed by "which column is this" (header lookup, resize target,
          // sticky-column-boundary check) must use the occupied grid position instead.
          const cellKey = `${props.propertyName ?? props.name}.${name}`;
          const trueColIdx = (cellColumnIndex || {})[cellKey] ?? cellIdx;
          const header = headers?.[trueColIdx] || {};

          const stickyInfo = (stickyCells || {})[cellKey];
          const cellProps = parseCellProps(header, fieldUiSchema, {
            stickyInfo,
            stickyColumns,
            columnWidth: getColumnWidth(trueColIdx),
            isSingleCellFullWidth,
            scrollWidth,
            cellIdx: trueColIdx,
            asHeader,
            isLastCellInRow: cellIdx + 1 >= _cellFields.length,
            isDarkMode,
            rowStyles: styles,
          });

          rowBg = cellIdx === 0 ? cellProps.style.background : rowBg;
          cellProps.style = { ...cellProps.style, background: rowBg };

          const cellClassName = classnames('object-table-cell', cellProps.className, {
            'py-4': ref != null,
          });

          return (
            <StyledTd
              key={name}
              header={header}
              {...(ref != null ? { ref } : {})}
              uiSchema={fieldUiSchema}
              rowBg={rowBg}
              hoverBg={isDarkMode ? RAISED : LIGHT_HOVER}
              data-col-index={trueColIdx}
              {...cellProps}
              className={classnames(cellClassName, {
                nowrap: horizontalScroll && !cellClassName.includes('wrap-cell'),
                // ['body-cell']: stickyColumns > 0 && !asHeader
              })}
              darkMode={isDarkMode}
            >
              <SchemaField
                name={`${propertyName}.${name}`}
                required={isRequired(name, schema)}
                schema={{ ...fieldSchema }}
                uiSchema={{ ...fieldUiSchema }}
                errorSchema={(errorSchema || {})[name]}
                fieldPathId={{ $id: `${fieldPathId.$id}_${name}`, path: [...fieldPathId.path, name] }}
                formData={(formData || {})[name]}
                onChange={(value, _path, propErrorSchema) => onPropertyChange(`${propertyName}.${name}`, value, propErrorSchema, _path)}
                onDropPropertyClick={key => onDropPropertyClick(key)}
                onBlur={onBlur}
                onFocus={onFocus}
                registry={registry}
                disabled={disabled}
                readonly={readonly}
                hideError={hideError}
              />
              {!isSingleCellFullWidth && (
                <div
                  className="object-table-col-resize-handle"
                  onPointerDown={event => onColumnResizeStart(event, trueColIdx)}
                  onDoubleClick={() => onResetColumnWidth(trueColIdx)}
                />
              )}
            </StyledTd>
          );
        })}
        {hasButtons ? <td /> : null}
      </tr>
    );
  };

  if (isSingleCellFullWidth) {
    // Wrap in valid <tr><td> so a <div> is never a direct child of <tbody>.
    // The inner renderRow (<tr>) lives inside a nested <table><tbody> context.
    const colCount = _cellFields.length + (hasButtons ? 1 : 0);
    // scrollWidth starts as 'auto' (string) until the scroll container is measured.
    // Subtracting from a string produces NaN, which collapses the table to nothing.
    const innerTableWidth = typeof scrollWidth === 'number' && scrollWidth > 0 ? scrollWidth - 2 : undefined;
    const overlay = (
      <tr key={`${$id}-overlay`} aria-hidden="true" style={{ height: 0, overflow: 'visible' }}>
        <td colSpan={colCount} style={{ height: 0, padding: 0, border: 'none', overflow: 'visible', position: 'sticky', left: 0 }}>
          <div className="p-0" style={{ position: 'sticky', height: 0, left: 0, zIndex: 10, overflow: 'visible' }}>
            <table
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
                width: innerTableWidth,
                minWidth: innerTableWidth,
                maxWidth: innerTableWidth,
              }}
            >
              <tbody>{renderRow(`${$id}-visible`, {}, overlayCellRef)}</tbody>
            </table>
          </div>
        </td>
      </tr>
    );
    const overlayHeight = overlayCellRef?.current?.clientHeight ?? 'auto';
    return [overlay, renderRow(`${$id}-spacer`, { visibility: 'hidden', height: overlayHeight }, null)];
  }

  return renderRow($id);
}

/**
 * Custom hook providing the necessary event handlers and derived state for an object table row to ensure the
 * fields in the table rows works as expected.
 *
 * Some of the handlers passed down in props are overwritten. (is this necessary?)
 * - onDropPropertyClick
 *
 * @typedef {Object} Jnx
 * @property {(formData: any, fieldName: string) => boolean} eval - Evaluates a JSONata expression for the given field.
 *
 * @typedef {Object} UseObjectTableRowHookResult
 * @property {boolean} readonly - Whether the row’s fields are read-only.
 * @property {Record<string, boolean>} show - Map of field names to visibility flags.
 * @property {(name: string, value: any, errorSchema?: Record<string, any>) => void} onPropertyChange - Updates a single property.
 * @property {(key: string) => (event: Event) => void} onDropPropertyClick - Returns an event handler that deletes a property.
 *
 * @param {Object} props
 * @param {Object} props.schema - JSON schema describing the row’s structure.
 * @param {Object} props.uiSchema - UI schema for customizing row appearance.
 * @param {Object} props.idSchema - Schema for field IDs.
 * @param {Object} props.errorSchema - Validation errors for this row.
 * @param {any} props.formData - Current form data for this row (JSON).
 * @param {Object} props.formContext - Arbitrary form-level context object.
 * @param {Object} props.registry - Registry of fields, widgets, and templates.
 * @param {Function} props.onChange - Callback to propagate form data changes.
 * @param {Function} props.onBlur - Callback when a field loses focus.
 * @param {Function} props.onFocus - Callback when a field gains focus.
 * @param {Function} props.onDropPropertyClick
 * @param {boolean} props.disabled - Whether fields are disabled.
 * @param {boolean} props.readonly - Whether fields are read-only.
 * @param {boolean} props.hideError - Whether to hide validation errors.
 * @param {boolean} [props.isReadOnly] - Alternative flag for read-only state (can be missing).
 * @param {string} [props.idPrefix] - Prefix for field IDs (can be missing).  TO BE TESTED.
 * @param {string} [props.idSeparator] - Separator for ID segments (can be missing).  TO BE TESTED.
 * @param {Record<string, Jnx>} [props.showColumnsIf] - Optional per-field visibility rules (can be missing). TO BE TESTED.
 *
 * @returns {UseObjectTableRowHookResult}
 */
function useObjectTableRowHooks(props) {
  const {
    registry,
    disabled,
    readonly: readonlyProp,
    isReadOnly,
    hideError,
    formData,
    onBlur,
    onFocus,
    onChange,
    uiSchema,
    errorSchema,
    fieldPathId: _fieldPathId,
    schema,
    showColumnsIf,
    propertyName,
    formDataRef,
  } = props;
  const formContext = registry?.formContext;

  const fieldPathId = _fieldPathId
    ? {
        $id: `${_fieldPathId?.$id + '_' || ''}${propertyName}`,
        path: [...(_fieldPathId?.path || []), propertyName],
      }
    : {
        $id: propertyName,
        path: [propertyName],
      };

  const { properties: childFields } = schema;

  const readonly = readonlyProp || isReadOnly;

  function onDropPropertyClick(key) {
    return event => {
      event.preventDefault();
      const copiedFormData = { ...formDataRef.current };
      delete copiedFormData[key];
      onChange(copiedFormData, fieldPathId.path);
    };
  }

  function onPropertyChange(name, value, propErrorSchema, path) {
    onChange(value, path, propErrorSchema && errorSchema && { ...errorSchema, [name]: propErrorSchema });
  }

  const show = useMemo(() =>
    Object.keys(childFields).reduce(
      (_, name) => {
        const showIf = (showColumnsIf || {})[name];
        _[name] = !showIf || showIf.eval(formData, name);

        return _;
      },
      [showColumnsIf, childFields, formData]
    )
  );

  return {
    readonly,
    show,
    onDropPropertyClick,
    onPropertyChange,
    fieldPathId,
  };
}

const useObjectTable = (rowFields, stickyColumnsFromSchema, stickyColumnWidth, stickyHeaders) => {
  const value = useMemo(() => {
    const columnCount = rowFields.reduce((max, r) => Math.max(max, Object.keys(r?.content?.props?.schema?.properties ?? {}).length), 0);
    const stickyColumns = stickyColumnsFromSchema > 0 ? stickyColumnsFromSchema : columnCount > 2 ? 1 : 0;

    // Always walk the colSpan/rowSpan-aware grid layout (not just when sticky columns
    // or headers are on) — cellColumnIndex is the TRUE occupied grid column for each
    // cell, which callers (resize, header lookup) need instead of a cell's raw position
    // within its own row's property list, since a colSpan/rowSpan earlier in the table
    // can shift later cells off of that naive position.
    //
    // Standard grid-position walk: each row keeps a column cursor that advances past
    // any column already reserved by an active rowSpan from an earlier row, then claims
    // the cell's entire span footprint (every row/column it covers, even a plain 1x1
    // cell) so a later cell in the SAME row that gets shifted is never able to land back
    // on a column its predecessor already claimed.
    let stickyCells = {};
    let cellColumnIndex = {};
    let takenCells = {};
    let processedRows = rowFields.map((row, rowIx) => {
      const rowSchema = row.content?.props?.schema ?? {};
      const cells = rowSchema?.properties ?? {};
      const cellKeys = Object.keys(cells);

      let nextColIx = 0;
      cellKeys.forEach(cellKey => {
        const { 'ui:rowSpan': cellRowSpan = 1, 'ui:colSpan': cellColSpan = 1 } = cells[cellKey] ?? {};

        while (takenCells[`${rowIx}.${nextColIx}`]) nextColIx++;
        const colIx = nextColIx;

        for (let dr = 0; dr < cellRowSpan; dr++) {
          for (let dc = 0; dc < cellColSpan; dc++) {
            takenCells[`${rowIx + dr}.${colIx + dc}`] = true;
          }
        }
        nextColIx += cellColSpan;

        cellColumnIndex[`${row.content.key}.${cellKey}`] = colIx;

        const inStickyColumn = isCellInStickyColumn(stickyColumns, colIx);
        const inStickyRow = isCellInStickyRow(stickyHeaders, rowSchema['ui:asHeader'] ?? false, rowIx);

        if (inStickyColumn || inStickyRow) {
          stickyCells[`${row.content.key}.${cellKey}`] = { row: inStickyRow, col: inStickyColumn };
        }
      });

      return {
        ...row,
      };
    });

    return { rows: processedRows, stickyCells, cellColumnIndex, columnCount, stickyColumns };
  }, [rowFields, stickyColumnsFromSchema, stickyColumnWidth, stickyHeaders]);

  return value;
};

export default ObjectTable;
